<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { onboardingSave } from '@/services/onboarding.service'
import OnboardingPersonalDetailsBanner from '@/components/header/auth/AuthOnboardingAdvBanner/OnboardingPersonalDetailsBanner.vue'
import { ONBOARDING_STEPS } from '@/components/header/auth/OnboardingFlow/enum/onboarding.enum'
import BaseInput from '@/components/forms/BaseInput.vue'
import AuthOnboardingLabel from '@/components/header/auth/AuthOnboardingLabel.vue'
import type { IOnboardingSave } from '@/components/organisation/interface/IOnboardingSteps'
import Button from '@/components/atoms/button/index.vue'
import { HttpStatusCode } from 'axios'
import { useField, useForm } from 'vee-validate'
import { string } from 'yup'
import { useAuthStore } from '@/stores/auth.store'

const props = defineProps({
  currentOnboardingSteps: {
    type: Function,
    required: true
  },
  prefilledValues: {
    type: Object,
    required: false
  }
})
const { errors, handleSubmit } = useForm()
const { value: firstname, errorMessage: firstNameError } = useField(
  'firstname',
  string().required().min(1)
)
const { value: lastname, errorMessage: lastNameError } = useField('lastname', string())

const isLoading = ref<boolean>(false)
const httpError = ref<string>('')
const authStore = useAuthStore()

const canSubmit = computed(() => {
  return Object.keys(errors.value).length === 0 && !isLoading.value
})

const onSubmit = handleSubmit(async (values) => {
  let requestObj: IOnboardingSave = {
    firstName: values.firstname,
    lastName: values.lastname,
    onboarded: false,
    step: 2
  }
  isLoading.value = true
  httpError.value = ''
  await onboardingSave(requestObj)
    .then((res: any) => {
      if (res?.status == HttpStatusCode.Ok) {
        props.currentOnboardingSteps(ONBOARDING_STEPS.PROFESSIONAL, requestObj)
      }
    })
    .catch(() => {
      httpError.value = 'Something went wrong please try again'
    })
    .finally(() => {
      isLoading.value = false
    })
})

/**
 * Checking the data for first name and last name
 */
const checkingFirstLastName = () => {
  firstname.value = props?.prefilledValues?.firstName || authStore?.onboardingSteps?.firstName
  lastname.value = props?.prefilledValues?.lastName || authStore?.onboardingSteps?.lastName || ''
}

onMounted(() => {
  checkingFirstLastName()
})

watch(
  () => [authStore?.onboardingSteps?.firstName, props?.prefilledValues?.firstName],
  () => {
    checkingFirstLastName()
  }
)
</script>

<template>
  <div class="flex h-screen w-full flex-col">
    <div class="flex w-full flex-1 p-1 sm:p-5">
      <div class="hidden w-full rounded-3xl bg-[#EBEFF6] md:hidden lg:block lg:min-w-[45%]">
        <OnboardingPersonalDetailsBanner />
      </div>

      <div class="mt-5 h-full w-full rounded-md lg:min-w-[50%]">
        <div class="flex w-full items-center justify-center p-5">
          <div class="w-full lg:w-[500px]">
            <AuthOnboardingLabel
              title="Welcome to JDoodle"
              description="We need to ask you a few questions to personalize your experience."
              customstyle="mt-5 text-lg font-medium leading-[27px]"
              customstyleTitle="text-3xl font-medium leading-10"
            />
          </div>
        </div>

        <div class="p-5">
          <!-- form -->
          <div class="flex w-full flex-col items-center justify-center">
            <div class="w-full lg:w-[500px]">
              <form @submit.prevent="onSubmit">
                <BaseInput
                  label="First Name"
                  :inputType="'text'"
                  placeholder="What do we call you?"
                  autocomplete="email"
                  :error="firstNameError"
                  v-model="firstname"
                  :isBoldLabel="true"
                  :isLightMode="true"
                  :isLightGray="false"
                />

                <BaseInput
                  id="onb_login_pwd"
                  label="Last Name (optional)"
                  :inputType="'text'"
                  placeholder="Your surname goes here"
                  autocomplete="current-password"
                  :error="lastNameError"
                  v-model="lastname"
                  :isBoldLabel="true"
                  :isLightMode="true"
                  :isLightGray="false"
                />

                <p :class="['mt-10', 'p-normal', 'text-center', 'error', , { hidden: !httpError }]">
                  {{ httpError }}
                </p>
                <p
                  :class="[
                    'mt-10',
                    'p-normal',
                    'text-sky-950',
                    'text-center',
                    { hidden: !isLoading }
                  ]"
                >
                  Updating your details. Please wait...
                </p>
                <div class="flex justify-center sm:mt-9">
                  <Button
                    type="submit"
                    :disabled="!canSubmit"
                    color="secondary"
                    variant="filled"
                    size="medium"
                    class="mt-6 h-[48px] sm:w-[171px]"
                  >
                    Continue
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
